import {
  CalendarClockIcon,
  ChevronRightIcon,
  InfoIcon,
  type LucideIcon,
  MapIcon,
  MessagesSquareIcon,
  PhoneCallIcon,
  StoreIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import client from "@/api/client";
import type {
  EmployeeInfo,
  ExtraShiftWeek,
  Store,
} from "../api/QuikTripClient";
import { separateName, toTitleCase } from "../lib/util";

const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

interface Props {
  employeeInfo: EmployeeInfo | null;
}

function OptionRow({
  icon: Icon,
  title,
  description,
  onClick,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="active:bg-base-300 flex w-full items-center gap-3 px-4 py-3 text-left transition-colors"
    >
      <div className="bg-primary/15 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
        <Icon size={20} className="text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-base-content text-sm font-semibold">{title}</div>
        <div className="text-base-content/50 mt-0.5 text-xs">{description}</div>
      </div>
      <ChevronRightIcon size={16} className="text-base-content/30 shrink-0" />
    </button>
  );
}

export function StoresPage({
  setPageType,
  employeeInfo,
}: {
  setPageType: (type: "home" | "stores") => void;
  employeeInfo: EmployeeInfo;
}) {
  const [storesLoading, setStoresLoading] = useState<boolean>(true);
  const [stores, setStores] = useState<Store[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  useEffect(() => {
    client
      .getDivisionStores(employeeInfo.division)
      .then((s) => setStores(s))
      .catch(() => {})
      .finally(() => setStoresLoading(false));
  }, [employeeInfo.division]);

  const filteredStores = useMemo(() => {
    const query = searchQuery.toLowerCase();
    if (!query) return stores;
    return stores.filter(
      (store) =>
        store.storeNumber.toString().includes(query) ||
        store.address.toLowerCase().includes(query),
    );
  }, [stores, searchQuery]);

  return (
    <div className="flex h-full flex-col">
      <div className="border-base-300 flex items-center gap-3 border-b px-4 py-3">
        <button
          type="button"
          onClick={() => setPageType("home")}
          className="active:bg-base-300 rounded-md p-1 transition-colors"
        >
          <ChevronRightIcon
            size={20}
            className="text-base-content rotate-180"
          />
        </button>
        <h1 className="text-base-content text-lg font-semibold">Stores</h1>
      </div>
      <div className="px-4 py-3">
        <input
          type="text"
          placeholder="Search stores..."
          className="input input-bordered w-full"
          onChange={(i) => setSearchQuery(i.target.value)}
        />
      </div>

      <div className="flex-1 overflow-hidden">
        {storesLoading ? (
          <div className="flex h-full items-center justify-center">
            <span className="loading loading-spinner loading-md text-primary" />
          </div>
        ) : (
          <div className="divide-base-300 h-full divide-y overflow-y-auto">
            {filteredStores.map((store) => (
              <div
                key={store.storeNumber}
                className="active:bg-base-300 flex flex-row justify-between px-4 py-3 transition-colors"
              >
                <div className="px-1">
                  <div className="text-base-content text-sm font-medium">
                    #{store.storeNumber} - {store.address}
                  </div>
                  <div className="text-base-content/50 mt-0.5 text-xs">
                    {store.city}, {store.state} {store.zip}
                  </div>
                </div>

                <div className="flex flex-row gap-2">
                  <a
                    href={
                      isIOS
                        ? `maps://maps.apple.com/?q=${encodeURIComponent(store.address)}`
                        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(store.address)}`
                    }
                    target="_blank"
                    rel="noopener"
                    className="btn btn-primary"
                  >
                    <MapIcon size={16} />
                    Directions
                  </a>
                  <a href={`tel:${store.phone}`} className="btn btn-secondary">
                    <PhoneCallIcon size={16} />
                    Call
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function shiftDuration(startTime: string, endTime: string): string {
  const diffMs = new Date(endTime).getTime() - new Date(startTime).getTime();
  const hours = Math.floor(diffMs / 3_600_000);
  const mins = Math.floor((diffMs % 3_600_000) / 60_000);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function ExtraShiftPage({
  setPageType,
}: {
  setPageType: (type: "home" | "extraShifts" | "stores") => void;
}) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [extraShiftsLoading, setExtraShiftsLoading] = useState<boolean>(true);
  const [weeks, setWeeks] = useState<ExtraShiftWeek[]>([]);

  useEffect(() => {
    setExtraShiftsLoading(true);
    client
      .getExtraShiftsByWeek()
      .then((w) => setWeeks(w))
      .catch((e) => console.error("Error fetching extra shifts:", e))
      .finally(() => setExtraShiftsLoading(false));
  }, []);

  useEffect(() => {
    setExpandedId(null);
  }, []);

  const currentWeek = weeks[weekOffset];
  const maxOffset = Math.max(0, weeks.length - 1);
  const weekLabel = currentWeek?.startOfWeek
    ? `Week of ${currentWeek.startOfWeek}`
    : weekOffset === 0
      ? "This Week"
      : "Next Week";

  const shiftsByDay = useMemo(() => {
    if (!currentWeek) return [];
    const map = new Map<
      string,
      { label: string; shifts: typeof currentWeek.extraShifts }
    >();
    for (const shift of currentWeek.extraShifts) {
      const d = new Date(shift.startTime);
      const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (!map.has(key)) {
        map.set(key, {
          label: d.toLocaleDateString("en-US", {
            weekday: "long",
            month: "short",
            day: "numeric",
          }),
          shifts: [],
        });
      }
      map.get(key)?.shifts.push(shift);
    }
    return [...map.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, v]) => v);
  }, [currentWeek]);

  return (
    <div className="flex h-full flex-col">
      <div className="border-base-300 flex items-center gap-3 border-b px-4 py-3">
        <button
          type="button"
          onClick={() => setPageType("home")}
          className="active:bg-base-300 rounded-md p-1 transition-colors"
        >
          <ChevronRightIcon
            size={20}
            className="text-base-content rotate-180"
          />
        </button>
        <h1 className="text-base-content text-lg font-semibold">
          Extra Shifts
        </h1>
      </div>

      <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-3">
        <div className="bg-base-200 flex items-center justify-between rounded-xl px-4 py-2">
          <button
            className="btn btn-ghost btn-sm text-primary px-2 text-xl disabled:opacity-30"
            onClick={() => setWeekOffset((o) => Math.max(0, o - 1))}
            disabled={weekOffset === 0}
            aria-label="Previous week"
            type="button"
          >
            ‹
          </button>
          <span className="text-sm font-semibold">{weekLabel}</span>
          <button
            className="btn btn-ghost btn-sm text-primary px-2 text-xl disabled:opacity-30"
            onClick={() => setWeekOffset((o) => Math.min(maxOffset, o + 1))}
            disabled={weekOffset === maxOffset}
            aria-label="Next week"
            type="button"
          >
            ›
          </button>
        </div>

        {extraShiftsLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <span className="loading loading-spinner loading-md text-primary" />
          </div>
        ) : shiftsByDay.length === 0 ? (
          <div className="text-base-content/40 py-8 text-center text-sm">
            No extra shifts available
          </div>
        ) : (
          shiftsByDay.map((day) => (
            <div key={day.label} className="flex flex-col gap-2">
              <div className="text-base-content/50 px-1 text-xs font-semibold tracking-wider uppercase">
                {day.label}
              </div>
              {day.shifts.map((shift) => {
                const expanded = expandedId === shift.extraShiftId;
                const mapsUrl = isIOS
                  ? `maps://maps.apple.com/?ll=${shift.latitude},${shift.longitude}`
                  : `https://www.google.com/maps/search/?api=1&query=${shift.latitude},${shift.longitude}`;
                return (
                  <div
                    key={shift.extraShiftId}
                    className="bg-base-200 rounded-xl border-l-4 border-l-[var(--color-primary)]"
                  >
                    <div className="flex items-stretch">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedId(expanded ? null : shift.extraShiftId)
                        }
                        className="active:bg-base-300 min-w-0 flex-1 px-4 py-3 text-left transition-colors"
                      >
                        <div className="text-sm font-semibold">
                          #{shift.storeNumber}
                        </div>
                        <div className="text-base-content/60 mt-1 text-xs">
                          {shift.displayTime}
                        </div>
                        <div className="mt-1 flex gap-1">
                          {shift.isER && (
                            <span className="badge badge-xs badge-warning">
                              ER
                            </span>
                          )}
                          {shift.isTimeOff && (
                            <span className="badge badge-xs badge-info">
                              Time Off
                            </span>
                          )}
                        </div>
                      </button>
                      <div className="flex shrink-0 flex-col items-end justify-center gap-1.5 px-3 py-3">
                        <div className="flex items-center gap-1">
                          <span className="text-base-content/50 text-xs">
                            {shift.distance.toFixed(1)} mi
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              const popover = document.getElementById(
                                `dist-info-${shift.extraShiftId}`,
                              ) as HTMLElement & { showPopover: () => void };
                              if (!popover?.showPopover) return;
                              const rect =
                                e.currentTarget.getBoundingClientRect();
                              popover.style.margin = "0";
                              popover.style.top = `${rect.top + rect.height / 2}px`;
                              popover.style.left = `${rect.left - 8}px`;
                              popover.style.transform =
                                "translate(-100%, -50%)";
                              popover.showPopover();
                            }}
                            className="text-base-content/30 active:text-base-content/60 flex items-center transition-colors"
                            aria-label="Distance info"
                          >
                            <InfoIcon size={12} />
                          </button>
                          <div
                            id={`dist-info-${shift.extraShiftId}`}
                            popover="auto"
                            style={{ margin: 0 }}
                            className="bg-base-300 text-base-content border-base-content/10 w-44 rounded-xl border px-3 py-2 text-xs shadow-lg"
                          >
                            Distance is calculated from your assigned store, not
                            your current location.
                          </div>
                        </div>
                        <ChevronRightIcon
                          size={14}
                          className={`text-base-content/30 transition-transform ${expanded ? "rotate-90" : ""}`}
                        />
                      </div>
                    </div>

                    {expanded && (
                      <div className="border-base-300 flex flex-col gap-2 border-t px-4 py-3">
                        <div className="flex justify-between text-xs">
                          <span className="text-base-content/50">Duration</span>
                          <span className="font-medium">
                            {shiftDuration(shift.startTime, shift.endTime)}
                          </span>
                        </div>
                        {shift.reasonForRemoval && (
                          <div className="flex justify-between text-xs">
                            <span className="text-base-content/50">Reason</span>
                            <span className="text-error font-medium">
                              {shift.reasonForRemoval}
                            </span>
                          </div>
                        )}
                        <a
                          href={mapsUrl}
                          target="_blank"
                          rel="noopener"
                          className="btn btn-sm btn-outline btn-primary mt-1 w-full"
                        >
                          <MapIcon size={14} />
                          Directions
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export function HomePage({ employeeInfo }: Props) {
  const OPTIONS: {
    icon: LucideIcon;
    title: string;
    description: string;
    onClick: () => void;
  }[] = [
    {
      icon: StoreIcon,
      title: "Stores",
      description: "View and search stores",
      onClick: () => {
        setPageType("stores");
      },
    },
    {
      icon: MessagesSquareIcon,
      title: "The Register",
      description: "Go to the register website",
      onClick: () => {
        window.open("https://register.quiktrip.com/", "_blank");
      },
    },
    {
      icon: CalendarClockIcon,
      title: "Extra Shifts",
      description: "View and manage extra shifts",
      onClick: () => {
        setPageType("extraShifts");
      },
    },
  ];
  const [pageType, setPageType] = useState<"home" | "stores" | "extraShifts">(
    "home",
  );
  if (!employeeInfo) {
    return (
      <div className="flex h-full items-center justify-center">
        <span className="loading loading-spinner loading-md text-primary" />
      </div>
    );
  }
  const { firstName, middleInit, lastName } = separateName(employeeInfo.name);
  const first = toTitleCase(firstName);
  const fullName = toTitleCase(
    `${firstName}${middleInit ? ` ${middleInit}` : ""} ${lastName}`,
  );
  const initials = `${firstName[0] ?? ""}${lastName[0] ?? ""}`;
  if (pageType === "stores") {
    return <StoresPage setPageType={setPageType} employeeInfo={employeeInfo} />;
  } else if (pageType === "extraShifts") {
    return <ExtraShiftPage setPageType={setPageType} />;
  }
  return (
    <div className="flex h-full flex-col gap-6 px-5 pt-10 pb-6">
      <div>
        <p className="text-base-content/50 text-sm tracking-wide">
          {greeting()},
        </p>
        <h1 className="text-base-content mt-0.5 text-3xl leading-tight font-black tracking-tight">
          {first}.
        </h1>
      </div>

      <div
        className="overflow-hidden rounded-2xl shadow-xl"
        style={{
          background: "linear-gradient(145deg, #c8102e 0%, #7a0018 100%)",
        }}
      >
        <div className="flex items-center gap-4 px-5 pt-5 pb-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-white/25 bg-white/15">
            <span className="text-xl font-black text-white">{initials}</span>
          </div>
          <div className="min-w-0">
            <div className="truncate text-lg leading-tight font-bold text-white">
              {fullName}
            </div>
            <div className="mt-0.5 truncate text-sm text-white/65">
              {employeeInfo.job_Type_Desc}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between bg-black/25 px-5 py-3">
          <div>
            <div className="text-xs tracking-widest text-white/50 uppercase">
              Store
            </div>
            <div className="text-sm font-semibold text-white">
              #{employeeInfo.base_Store}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs tracking-widest text-white/50 uppercase">
              Division
            </div>
            <div className="max-w-36 truncate text-sm font-semibold text-white">
              {employeeInfo.division}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-base-200 divide-base-300 divide-y overflow-hidden rounded-xl">
        {OPTIONS.map((opt) => (
          <OptionRow key={opt.title} {...opt} />
        ))}
      </div>
    </div>
  );
}
